
import { useState, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@supabase/auth-helpers-react';
import { startGameSession } from '@/services/progressService';
import { getAnonymousPlayedGamesCount } from '@/services/anonymousTracking';
import { recordAnalyticsEvent } from '@/services/analyticsService';
import { games as localGames, findGameBySlugOrId } from '@/data/games';
import { handleError } from '@/utils/error-handling';

// Types
export interface GameData {
  id: number;
  title: string;
  slug: string;
  duration: string;
  description: string;
  url: string;
  isCustom: boolean;
}

// Max number of games a non-authenticated user can play
const MAX_FREE_GAMES = 2;

export const useGameLoader = (gameSlugOrId: string | undefined) => {
  const navigate = useNavigate();
  const session = useSession();
  const { toast } = useToast();
  const [game, setGame] = useState<GameData | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    // Check if gameSlugOrId is provided
    if (!gameSlugOrId) {
      toast({
        title: "Game not found",
        description: "No game identifier was provided.",
        variant: "destructive",
      });
      navigate('/');
      return;
    }
    
    const loadGameData = async () => {
      try {
        console.log(`Loading game with identifier: ${gameSlugOrId}`);
        
        // Find the game by slug or ID
        const localGame = findGameBySlugOrId(gameSlugOrId);
        
        if (!localGame) {
          const error = new Error(`Game with identifier ${gameSlugOrId} not found`);
          toast({
            title: "Game not found",
            description: "The selected game could not be found.",
            variant: "destructive",
          });
          navigate('/');
          setLoading(false);
          throw error;
        }
        
        // Check if non-authenticated user has reached the limit
        if (!session && localGame.id > MAX_FREE_GAMES) {
          try {
            // Get the count of games played by IP address
            const playedGamesCount = await getAnonymousPlayedGamesCount();
            
            if (playedGamesCount >= MAX_FREE_GAMES) {
              const error = new Error("Game limit reached for anonymous users");
              toast({
                title: "Game limit reached",
                description: "Create a free account to unlock all games and save your progress.",
                variant: "destructive",
              });
              navigate('/');
              setLoading(false);
              throw error;
            }
          } catch (countError) {
            // Handle error from getAnonymousPlayedGamesCount
            console.error("Failed to get played games count:", countError);
            // Just log the error but don't block game loading
          }
        }
        
        // We have a valid game, set it immediately
        setGame(localGame);
        setStartTime(new Date());
        
        // Record session for authenticated users
        if (session) {
          try {
            const sessionData = await startGameSession(localGame.id, localGame.title);
            if (sessionData) {
              setSessionId(sessionData.id);
              
              // Record analytics event for game start
              recordAnalyticsEvent('game_start', {
                game_id: localGame.id,
                game_title: localGame.title,
                session_id: sessionData.id,
                browser: navigator.userAgent // Add browser info for debugging
              });
            }
          } catch (sessionError) {
            console.error("Failed to record game session start:", sessionError);
            // Show error toast only for authenticated users
            if (session) {
              toast({
                title: "Error",
                description: "Failed to record game session start.",
                variant: "destructive",
              });
            }
            // Don't block the game from loading due to session recording failure
          }
        } else {
          // Record anonymous game start analytics
          recordAnalyticsEvent('game_start', {
            game_id: localGame.id,
            game_title: localGame.title,
            anonymous: true,
            browser: navigator.userAgent // Add browser info for debugging
          });
        }
        
        toast({
          title: "Game loaded",
          description: `Now playing: ${localGame.title}`,
        });
        
        setLoading(false);
      } catch (error) {
        console.error("Error loading game data:", error);
        setLoadError(error instanceof Error ? error.message : "Failed to load the game data");
        
        // Record error in analytics
        recordAnalyticsEvent('game_error', {
          error: error instanceof Error ? error.message : "Unknown error",
          game_id_or_slug: gameSlugOrId,
          browser: navigator.userAgent
        });
        
        setLoading(false);
      }
    };

    loadGameData();
  }, [gameSlugOrId, navigate, toast, session]);

  return { game, loading, sessionId, startTime, setSessionId, loadError };
};
